import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Maximize2, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/app/AuthProvider';
import { useProfiles } from '@/features/users/hooks';
import { useItems } from '@/features/inventory/hooks';
import { useOfficeAvatars } from '@/features/digitalOffice/hooks';
import { seededAvatar } from '@/features/digitalOffice/avatars';
import {
  useCredentialStatus,
  useFloorWorkstations,
  useOpenSessions,
  useStartSession,
  useStationOpenSession,
  useWorkProfileStats,
  useWorkRealtime,
} from '@/features/workMode/hooks';
import { useBoundStation } from '@/features/workMode/kiosk';
import { canWrite } from '@/lib/permissions';
import type { WorkProfileStats, WorkSession, WorkSessionStartResult, WorkShiftSummary as Summary } from '@/features/workMode/types';
import { WorkStationSetup } from '@/components/work/WorkStationSetup';
import { WorkLockScreen, type LockScreenPerson } from '@/components/work/WorkLockScreen';
import { WorkCheckinScreen } from '@/components/work/WorkCheckinScreen';
import { WorkBench } from '@/components/work/WorkBench';
import { WorkShiftSummary } from '@/components/work/WorkShiftSummary';

type Screen =
  | { kind: 'setup' }
  | { kind: 'lock' }
  | { kind: 'checkin'; session: WorkSession }
  | { kind: 'bench'; session: WorkSession }
  | { kind: 'summary'; summary: Summary; statsBefore: WorkProfileStats | undefined; userId: string; name: string };

/**
 * /work — Work Mode. The full-screen tablet experience for a workstation.
 * No app shell, no sidebar: setup → lock → check-in → bench → summary.
 *
 * The tablet itself is signed in to Atlas as one account; workers identify
 * themselves with a PIN or badge and the server ties every write to this
 * device + session (migration 116).
 */
export function WorkModePage() {
  const { user, profile, role } = useAuth();
  const canManage = canWrite(role, 'manufacturing');
  const [stationId, bindStation] = useBoundStation();
  const [screen, setScreen] = useState<Screen>({ kind: stationId ? 'lock' : 'setup' });
  const [resumeChecked, setResumeChecked] = useState(false);

  useWorkRealtime(true);

  const { data: stations, isLoading: stationsLoading } = useFloorWorkstations();
  const station = useMemo(() => stations?.find((s) => s.id === stationId) ?? null, [stations, stationId]);
  const { data: profiles = [] } = useProfiles();
  const { data: avatars = [] } = useOfficeAvatars();
  const { data: credentials } = useCredentialStatus();
  const { data: openSessions = [] } = useOpenSessions();
  const { data: openHere, isFetched: openHereFetched } = useStationOpenSession(stationId);
  const { data: items = [] } = useItems();
  const start = useStartSession();

  // Bound station vanished or was switched off -> back to setup.
  useEffect(() => {
    if (!stationId || stationsLoading || !stations) return;
    if (!station || !station.is_active) {
      bindStation(null);
      setScreen({ kind: 'setup' });
    }
  }, [stationId, stations, stationsLoading, station, bindStation]);

  // A reloaded tablet picks its open shift back up.
  useEffect(() => {
    if (resumeChecked || !openHereFetched || !user) return;
    setResumeChecked(true);
    if (openHere && openHere.kiosk_user_id === user.id && screen.kind === 'lock') {
      setScreen({ kind: 'bench', session: openHere });
    }
  }, [openHere, openHereFetched, resumeChecked, user, screen.kind]);

  const stationNames = useMemo(() => new Map((stations ?? []).map((s) => [s.id, s.name])), [stations]);
  const avatarByUser = useMemo(() => new Map(avatars.map((a) => [a.userId, a])), [avatars]);
  const itemName = useCallback((id: string | null) => (id ? (items.find((i) => i.id === id)?.name ?? null) : null), [items]);
  const itemSerialized = useCallback((id: string | null) => (id ? !!items.find((i) => i.id === id)?.is_serialized : false), [items]);

  const people = useMemo<LockScreenPerson[]>(() => {
    const working = new Map(openSessions.map((s) => [s.user_id, s.workstation_id]));
    return profiles
      .filter((p) => p.is_active)
      .map((p) => {
        const cred = credentials?.get(p.id);
        return {
          id: p.id,
          name: p.full_name || p.email || 'Teammate',
          avatarUrl: p.avatar_url ?? null,
          config: avatarByUser.get(p.id)?.config ?? seededAvatar(p.id),
          hasPin: cred?.has_pin ?? false,
          hasBadge: cred?.has_badge ?? false,
          lockedUntil: cred?.locked_until ?? null,
          workingAt: working.has(p.id) ? (stationNames.get(working.get(p.id)!) ?? 'another station') : null,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [profiles, credentials, avatarByUser, openSessions, stationNames]);

  const workerFor = useCallback(
    (userId: string) => {
      const p = people.find((x) => x.id === userId);
      return p ? { name: p.name, avatarUrl: p.avatarUrl, config: p.config } : { name: 'Teammate', avatarUrl: null, config: seededAvatar(userId) };
    },
    [people],
  );

  const handleStart = useCallback(
    async (input: { userId?: string | null; pin?: string | null; badge?: string | null }): Promise<WorkSessionStartResult> => {
      if (!stationId) throw new Error('work_station_not_found');
      const res = await start.mutateAsync({ workstationId: stationId, ...input });
      if (res.ok) {
        setScreen(res.needs_checkin ? { kind: 'checkin', session: res.session } : { kind: 'bench', session: res.session });
      }
      return res;
    },
    [start, stationId],
  );

  // Stats are fetched while the worker is on the bench so the summary can
  // show what changed during the shift.
  const benchUserId = screen.kind === 'bench' || screen.kind === 'checkin' ? screen.session.user_id : null;
  const { data: statsLive } = useWorkProfileStats(benchUserId);
  const summaryUserId = screen.kind === 'summary' ? screen.userId : null;
  const { data: statsAfter } = useWorkProfileStats(summaryUserId);

  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      toast.message('Use the browser or kiosk app menu to go full screen on this device.');
    }
  };

  const content = (() => {
    if (screen.kind === 'setup' || !stationId) {
      return (
        <WorkStationSetup
          stations={stations}
          loading={stationsLoading}
          canManage={canManage}
          onBind={(id) => {
            bindStation(id);
            setScreen({ kind: 'lock' });
          }}
        />
      );
    }
    if (!station) return null;
    if (screen.kind === 'lock') {
      return (
        <WorkLockScreen
          station={station}
          itemName={itemName(station.default_item_id)}
          people={people}
          me={user && profile ? { id: user.id, name: profile.full_name || profile.email || 'me' } : null}
          busy={start.isPending}
          onStart={handleStart}
          onChangeStation={() => {
            bindStation(null);
            setScreen({ kind: 'setup' });
          }}
        />
      );
    }
    if (screen.kind === 'checkin') {
      const w = workerFor(screen.session.user_id);
      return <WorkCheckinScreen name={w.name} sessionId={screen.session.id} onDone={() => setScreen({ kind: 'bench', session: screen.session })} />;
    }
    if (screen.kind === 'bench') {
      const w = workerFor(screen.session.user_id);
      return (
        <WorkBench
          key={screen.session.id}
          session={screen.session}
          station={station}
          worker={w}
          itemName={itemName(screen.session.item_id)}
          serialized={itemSerialized(screen.session.item_id)}
          onEnded={(summary) =>
            setScreen({ kind: 'summary', summary, statsBefore: statsLive, userId: screen.session.user_id, name: w.name })
          }
          onLost={() => {
            toast.message('That shift was closed. Sign in again to continue.');
            setScreen({ kind: 'lock' });
          }}
        />
      );
    }
    return (
      <WorkShiftSummary
        name={screen.name}
        summary={screen.summary}
        statsBefore={screen.statsBefore}
        statsAfter={statsAfter}
        onDone={() => setScreen({ kind: 'lock' })}
      />
    );
  })();

  return (
    <div className="flex h-dvh flex-col bg-background text-foreground">
      <div className="flex h-9 shrink-0 items-center justify-between border-b border-border bg-secondary/60 px-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <span className="font-medium tracking-wide text-foreground">Atlas · Work Mode</span>
          {station && screen.kind !== 'setup' && <span>{station.name}</span>}
        </div>
        <div className="flex items-center gap-3">
          <Clock />
          <button type="button" onClick={toggleFullscreen} className="inline-flex items-center gap-1 hover:text-foreground" aria-label="Toggle full screen">
            {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
          <Link to="/dashboard" className="hover:text-foreground">
            Exit to Atlas
          </Link>
        </div>
      </div>
      <main className="min-h-0 flex-1 overflow-y-auto">{content}</main>
    </div>
  );
}

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 10_000);
    return () => window.clearInterval(id);
  }, []);
  return <span className="tabular-nums">{now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>;
}
